import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { PLATFORM_BRANDING } from '../../../constants/branding';
import './AppLoader.css';

const DEFAULT_TITLE = PLATFORM_BRANDING.NAME;
const DEFAULT_SUBTITLE = "Sécurité et santé : main dans la main vers le succès";

const PARTICLE_LAYOUT = [
  { top: '18%', left: '22%', delay: '0s' },
  { top: '28%', right: '18%', delay: '1.2s' },
  { bottom: '32%', left: '14%', delay: '2.1s' },
  { bottom: '24%', right: '24%', delay: '0.6s' },
  { top: '42%', left: '8%', delay: '3s' },
  { top: '52%', right: '10%', delay: '1.8s' },
];

const shellTransition = {
  duration: 0.52,
  ease: [0.22, 1, 0.36, 1],
};

const cardTransition = {
  duration: 0.72,
  ease: [0.22, 1, 0.36, 1],
  delay: 0.06,
};

/**
 * Écran de chargement premium — logo FUNTOA, animations Framer Motion + CSS GPU.
 *
 * @param {'fullscreen'|'overlay'} variant
 * @param {'light'|'dark'|'auto'} theme
 */
export default function AppLoader({
  variant = 'fullscreen',
  theme = 'auto',
  logoSrc = '/logo.png',
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  statusMessage,
  className = '',
}) {
  const themeClass =
    theme === 'auto' ? 'app-loader--auto' : theme === 'dark' ? 'app-loader--dark' : 'app-loader--light';

  const particles = useMemo(
    () =>
      PARTICLE_LAYOUT.map((pos, i) => (
        <span
          key={i}
          className="app-loader__particle"
          style={{ ...pos, animationDelay: pos.delay }}
          aria-hidden="true"
        />
      )),
    []
  );

  const label = statusMessage || 'Chargement en cours';

  return (
    <motion.div
      className={`app-loader app-loader--${variant} ${themeClass} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        scale: 1.015,
        filter: 'blur(6px)',
        transition: shellTransition,
      }}
      transition={shellTransition}
    >
      <div className="app-loader__mesh" aria-hidden="true" />
      <motion.div
        className="app-loader__grain"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.03 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        className="app-loader__particles"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        {particles}
      </motion.div>

      <div className="app-loader__stage">
        <motion.div
          className="app-loader__card"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={cardTransition}
        >
          <motion.div
            className="app-loader__emblem"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: [0, -5, 0],
            }}
            transition={{
              opacity: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
              y: {
                duration: 3.6,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.9,
              },
            }}
          >
            <motion.div
              className="app-loader__orbit-field"
              aria-hidden="true"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            >
              <div className="app-loader__orbit app-loader__orbit--outer">
                <span className="app-loader__orbit-ring app-loader__orbit-ring--gradient" />
                <span className="app-loader__orbit-sat" />
                <span className="app-loader__orbit-sat app-loader__orbit-sat--offset" />
              </div>
              <div className="app-loader__orbit app-loader__orbit--mid">
                <span className="app-loader__orbit-ring app-loader__orbit-ring--dashed" />
                <span className="app-loader__orbit-sat app-loader__orbit-sat--accent" />
              </div>
              <div className="app-loader__orbit app-loader__orbit--inner">
                <span className="app-loader__orbit-ring app-loader__orbit-ring--arc" />
                <span className="app-loader__orbit-ring app-loader__orbit-ring--arc-reverse" />
              </div>
            </motion.div>
            <span className="app-loader__halo" aria-hidden="true" />
            <span className="app-loader__glow" aria-hidden="true" />
            <img src={logoSrc} alt="" className="app-loader__logo" width={68} height={68} decoding="async" />
          </motion.div>

          <motion.h1
            className="app-loader__title"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...cardTransition, delay: 0.22 }}
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              className="app-loader__subtitle"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...cardTransition, delay: 0.32 }}
            >
              {subtitle}
            </motion.p>
          )}

          {statusMessage && (
            <motion.p
              className="app-loader__status"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                opacity: { duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
              }}
            >
              {statusMessage}
            </motion.p>
          )}

          <div className="app-loader__track" aria-hidden="true">
            <span className="app-loader__bar" />
          </div>

          <motion.div
            className="app-loader__dots"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.4 }}
          >
            <span className="app-loader__dot" />
            <span className="app-loader__dot" />
            <span className="app-loader__dot" />
          </motion.div>
        </motion.div>
      </div>

      <span className="app-loader__sr">{label}</span>
    </motion.div>
  );
}
